export class ApiError extends Error {
    constructor(public statusCode: number, public statusText: string, message?: string) {
        super(message);
        this.name = 'ApiError';
    }
}

export interface Calendar {
    now: Date,
    instance_uuid: string,
    instance_metas: { [key: string]: string },
    calendar_uuid: string,
    calendar_metas: { [key: string]: string },
    dates: { [key: number]: { [key: number]: number[] } },
}

export interface CalendarEnum {
    now: string
    calendars: {
        [key: string]: CalendarEnumEntry
    }
}

export interface CalendarEnumEntry {
    name: string
    title: string
    uuid: string
    created_at: Date
    instances: {
        [key: string]: CalendarEnumInstance
    }
}

export interface CalendarEnumInstance {
    name: string
    title: string
    type_appel: string
    resolution: number
    uuid: string
    created_at: Date
}

export interface CalendarSlot {
    date: Date,
    duration: number,
    count: number,
}

export interface EventPerson {
    civility: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

export interface CalendarEvent {
    guid: string;
    date: Date;
    duration: number;
    instance_guid: string;
    persons: EventPerson[];
    canceled: boolean;
}

function build_queries(kv: { [key: string]: string }): string {
    return Object.keys(kv).map(k => `${k}=${kv[k]}`).join('&');
}

export async function api_update_instance(uuid: string, token: string, data: {[key:string]:string}): Promise<number> {
    console.log(`api_update_slots: ${uuid}: %o`, data);

    const myHeaders = new Headers();
    myHeaders.set('Content-Type', 'application/json');

    const myInit = {
        method: 'PATCH',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
        body: JSON.stringify(data),
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.status;
}

export async function api_get_slots_by_week(uuid: string, token: string, year: number, week: number, forceCount: number): Promise<CalendarSlot[]> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/slots/`;
    const q = build_queries({
        'year': year.toFixed(),
        'week': week.toFixed(),
        'force': forceCount.toFixed(),
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export async function api_update_slots(uuid: string, token: string, slots: CalendarSlot[]): Promise<number> {
    console.log(`api_update_slots: ${uuid}: %o`, slots);

    const myHeaders = new Headers();
    myHeaders.set('Content-Type', 'application/json');

    const myInit = {
        method: 'POST',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
        body: JSON.stringify(slots),
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/slots/`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.status;
}

export async function api_get_slots(uuid: string, token: string, year: number, month: number, day: number | undefined = undefined, day_count: number = -1): Promise<CalendarSlot[]> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/slots/`;
    const r: { [key:string]:string } = {
        'year': year.toFixed(),
        'month': month.toFixed(),
        'token': token,
    };
    if (day !== undefined) {
        r['day'] = day.toFixed();
    }
    if (day_count > 0) {
        r['day_count'] = day_count.toFixed();
    }
    const q = build_queries(r);
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export async function api_get_calendar(uuid: string, token: string): Promise<Calendar> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export async function api_enum_calendars(token: string): Promise<CalendarEnum> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/calendar/`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export async function api_get_events_by_week(uuid: string, token: string, year: number, week: number, forceCount: number, all: boolean = false): Promise<CalendarEvent[]> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/events/`;
    const q = build_queries({
        'year': year.toFixed(),
        'week': week.toFixed(),
        'force': forceCount.toFixed(),
        'all': all ? "true" : "false",
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export interface EventPayload {
    deferred_guid: string;
    deferred_time: string;
    account_guid?: string;
    contact_guid?: string;
    alternate_guid?: string;
}

export interface CalendarEventRequest {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    persons: EventPerson[];
    payload: EventPayload;
}

export interface RegisterData {
    id: string;
}

export async function api_register_event(uuid: string, token: string, new_event: CalendarEventRequest): Promise<RegisterData> {
    const myHeaders = new Headers();
    myHeaders.set('Content-Type', 'application/json');

    const myInit = {
        method: 'POST',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
        body: JSON.stringify(new_event),
    } as RequestInit;

    const uri = `/api/v1/calendar/${uuid}/events/`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", error.message);
    }

    console.log(`response.status: ${response.status}: ${response.ok}`);
    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export interface CRMContact {
    uuid: string
    civility: string
    first_name: string
    last_name: string
    email: string
    phone: string
}

export async function api_get_contact(uuid: string, token: string): Promise<CRMContact> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/contact/${uuid}`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}

export async function api_get_account_contacts(uuid: string, token: string): Promise<CRMContact[]> {
    const myHeaders = new Headers();

    const myInit = {
        method: 'GET',
        headers: myHeaders,
        mode: 'cors',
        cache: 'no-store',
    } as RequestInit;

    const uri = `/api/v1/account/${uuid}`;
    const q = build_queries({
        'token': token,
    });
    const request = new Request(`${uri}?${q}`, myInit);

    let response: Response;
    try {
        response = await fetch(request)
    } catch (error) {
        throw new ApiError(0, "Network Error", "Failed to connect to the API");
    }

    if (!response.ok) {
        throw new ApiError(response.status, response.statusText, `Error fetching data from ${uri}`);
    }

    return response.json();
}
